import { html, css, LitElement } from 'lit-element';
import { db } from './intrivity_app.js'; // Ensure this exports your Firestore instance
import { collection, getDocs, doc, query, setDoc, deleteDoc, where, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import uuidv4 from 'v4-uuid';
import './intrivity-admin-invitations.js';

// Import necessary MWC components
import '@material/mwc-button';
import '@material/mwc-dialog';
import '@material/mwc-textfield';
import '@material/mwc-list/mwc-list.js';
import '@material/mwc-list/mwc-list-item.js';
import '@material/mwc-snackbar';

import { INTRIVITY_CLIENTS_GROUPS_CLOSED } from './intrivity-admin-clients.js';
import { 
    emitEvent, loadJSONFromStorage, getJSONFromStorage, getScore, hasRole,
    INTRIVITY_LOAD_RESOURCES, INTRIVITY_LOAD_CLIENTS, INTRIVITY_LOAD_GROUPS, 
    INTRIVITY_GROUP_REPORTS_LOADED, INTRIVITY_GOOGLE_LOGGED_IN, 
    INTRIVITY_GOOGLE_LOGGED_OUT, INTRIVITY_LOGIN, INTRIVITY_LOGGED_IN, 
    INTRIVITY_LOGOUT, INTRIVITY_NAV_SELECT, INTRIVITY_PRINTABLE_TOGGLE, 
    INTRIVITY_GROUP_SELECT, INTRIVITY_MASQUERADE 
} from './utility.js'
import { t, Localized } from './i18n.js';

export class IntrivityAdminTeams extends Localized(LitElement) {
  static get properties() {
    return {
      teams: { type: Array },
      user_profile: { type: Object },
      client: { type: Object },
      group: { type: Object },
      mode: { type: String },
      _editTeamMembers: { type: Boolean },
      _selectedUsersGroup: { type: Object },
      _isDialogOpen: { type: Boolean },
      _editingTeam: { type: Object },
      _teamFormData: { type: Object },
      _isLoading: { type: Boolean },
      _errorMessage: { type: String },
      _feedbackMessage: { type: String },
      _teamUuid: { type: String },              // selected group for invitations
      _parentGroupUsers: { type: Array },
      _teamUsers: { type: Array }
    };
  }

  constructor() {
    super();
    this.teams = [];
    this._isDialogOpen = false;
    this._editingTeam = null;
    this._teamFormData = this._getEmptyTeamData();
    this._isLoading = false;
    this._editTeamMembers = false;
    this._errorMessage = '';
    this._feedbackMessage = '';
    this._parentGroupUsers = [];
    this._teamUsers = [];
  }

  _getEmptyTeamData() {
    return {
      uuid: uuidv4(),
      type: 'team',
      status: 'none',
      payment_amount: 0.00,
      payment_required: 0,
      name: '',
      description: '',
      code: this._generateRandomCode(),
      clientid: this.group ? this.group.clientid : (this.client ? this.client.uuid : ''),
      parent: this.group ? this.group.uuid: '',
      joining: 1,
      report: 1,
      group_compare: 1,
      norm_groups: 0,
      group_enabled: 1,
      printable_report: 1,
      disable_report_privacy: 0,
      ai_enabled: 0
    };
  }


    // Load top level groups for the client
    async _loadGroupTeams(group) {
        console.log(`Loading teams for group ID: ${group.uuid}`);
        const groupsRef = collection(db, "groups");
        const q = query(
            groupsRef,
            where("parent", "==", group.uuid),
            where("type", "==", "team")
        );

        try {
            const snapshot = await getDocs(q);
            const _teams = snapshot.docs.map(doc => doc.data());
            console.log(_teams);
            this.teams = _teams;
        } catch (error) {
            console.error("Error loading teams for " + group.uuid + ":", error);
            return [];
        }
    } 

    async _showUserManagement(team) {
        this._selectedUsersGroup = team;
        this._isLoading = true;
        await this._loadParentGroupUsers();
        await this._loadTeamUsers(team);
        this._isLoading = false;
    }

    // The Users button doubles as Close Users once this team's users are showing.
    _toggleUserManagement(team) {
        if (this._selectedUsersGroup && this._selectedUsersGroup.uuid == team.uuid) {
            this._selectedUsersGroup = undefined;
            this._editTeamMembers = false;
        } else {
            this._showUserManagement(team);
        }
    }

    async _loadParentGroupUsers() {
        if (!this.group || !this.group.uuid) return;
        const usersRef = collection(db, "users");
        const q = query(usersRef, where("groups", "array-contains", this.group.uuid));
        try {
            const snapshot = await getDocs(q);
            this._parentGroupUsers = snapshot.docs.map(doc => doc.data());
        } catch (error) {
            console.error("Error loading parent group users:", error);
        }
    }

    async _loadTeamUsers(team) {
        if (!team || !team.uuid) return;
        const usersRef = collection(db, "users");
        const q = query(usersRef, where("groups", "array-contains", team.uuid));
        try {
            const snapshot = await getDocs(q);
            this._teamUsers = snapshot.docs.map(doc => doc.data());
        } catch (error) {
            console.error("Error loading team users:", error);
            this._teamUsers = [];
        }
    }

    // Program managers can only manage team members who are also members of
    // the parent group -- a team member added some other way (e.g. directly
    // via intrivity-admin-users) is left alone for a PM to see or remove.
    // Other modes (client/superadmin) keep the full team roster.
    _removableTeamUsers() {
        if (this.mode !== 'pm') return this._teamUsers;
        return this._teamUsers.filter(teamUser =>
            this._parentGroupUsers.some(groupUser => groupUser.email === teamUser.email));
    }


  _generateRandomCode() {
    return Array.from({ length: 6 }, () => Math.random().toString(36)[2].toUpperCase()).join('');
  }

  _openAddDialog() {
    this._editingTeam = null;
    this._teamFormData = this._getEmptyTeamData();
    this._isDialogOpen = true;
  }

  _openEditDialog(group) {
    this._editingTeam = group;
    this._teamFormData = { ...group };
    this._isDialogOpen = true;
  }

  _closeDialog() {
    this._isDialogOpen = false;
    this._teamFormData = this._getEmptyTeamData();
  }

  _handleFormInput(e) {
    const target = e.target;
    let { name, value } = target;
    if (!name) {
        name = target.dataset.name;
    }
    // Trim whitespace if value is a string
    if (typeof value === 'string') {
      value = value.trim();
    }
    this._teamFormData = { ...this._teamFormData, [name]: value };
    if (name == 'payment_amount') {
        let pValue = value.replace(/[^0-9.]/g, ""); // Remove non-numeric and non-decimal characters
        if (pValue.includes(".")) {
            const parts = pValue.split(".");
            this._teamFormData[name] = Number(parts[0] + "." + parts[1].substring(0, 2)); // Limit to two decimal places
        } else {
            this._teamFormData[name] = Number(pValue + ".00");
        }          
    }
  }

  async _saveGroup() {
    const group = { ...this._teamFormData };
    if (!group.uuid) group.uuid = uuidv4();
    const previous = this._editingTeam;
    const groupRef = doc(db, "groups", group.uuid);
    await setDoc(groupRef, group);
    this._showFeedback(t('groups.saved', { name: group.name }));
    this._closeDialog();
    this._loadGroupTeams(this.group);

    // A newly set/changed due date can change which members' responses are
    // counted, and a changed privacy setting can leave real names baked
    // into an already-generated summary -- either way, any cached AI
    // Summary for this team is now stale.
    if (previous && ((group.due && group.due !== previous.due) ||
        group.disable_report_privacy != previous.disable_report_privacy)) {
      await this._clearAiSummariesForTeam(group.uuid);
    }
  }

  // Deletes every team member's cached AI Summary for this team so the
  // next request regenerates it against the new due-date cutoff.
  async _clearAiSummariesForTeam(teamUuid) {
    try {
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("groups", "array-contains", teamUuid));
      const snap = await getDocs(q);
      await Promise.all(snap.docs.map((userDoc) =>
          deleteDoc(doc(db, "users", userDoc.id, "ai_summaries", teamUuid))
      ));
      console.log(`Cleared cached AI summaries for team ${teamUuid} (${snap.docs.length} member(s)).`);
    } catch (error) {
      console.error("Error clearing AI summaries for team due-date change:", error);
    }
  }

  async _deleteGroup(group) {
    if (confirm(t('groups.confirmDelete', { name: group.name }))) {
      await deleteDoc(doc(db, "groups", group.uuid));
      this._showFeedback(t('groups.deleted', { name: group.name }));
      this._loadGroupTeams(this.group);
    }
  }

  async _updateTeam(team) {
    console.log("Updating team:", team);
    const groupRef = doc(db, "groups", team.uuid);
    await setDoc(groupRef, team);
    console.log("Updated team:", team);
    this._showFeedback(t('teamsAdmin.saved', { name: team.name }));
  }

  async _addUserToTeam(user, team) {
      if (!user || !user.email || !team || !team.uuid) return;
      const userRef = doc(db, "users", user.email);
      try {
          await updateDoc(userRef, {
              groups: arrayUnion(team.uuid),
              updated_at: new Date().toISOString()
          });
          this._showFeedback(t('teamsAdmin.userAdded', { user: user.displayName, team: team.name }));
          team.status = 'process';
          await this._updateTeam(team);
          await this._loadTeamUsers(team); // Refresh team user list
      } catch (error) {
          console.error("Error adding user to team:", error);
          this._showFeedback(t('teamsAdmin.addError'));
      }
  }

  async _removeUserFromTeam(user, team) {
    if (!user || !user.email || !team || !team.uuid) return;
    if (confirm(t('teamsAdmin.confirmRemove', { user: user.displayName, team: team.name }))) {
        const userRef = doc(db, "users", user.email);
        try {
            await updateDoc(userRef, {
                groups: arrayRemove(team.uuid),
                updated_at: new Date().toISOString()
            });
            this._showFeedback(t('teamsAdmin.userRemoved', { user: user.displayName, team: team.name }));
            team.status = 'process';
            await this._updateTeam(team);
            await this._loadTeamUsers(team); // Refresh team user list
        } catch (error) {
            console.error("Error removing user from team:", error);
            this._showFeedback(t('teamsAdmin.removeError'));
        }
    }
  }

  _showFeedback(message) {
    this._feedbackMessage = message;
    alert(message);
//    this.shadowRoot.getElementById('feedback-snackbar').open();
  }

  static get styles() {
    return css`
      .group-item {
        width: 100%;
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        align-items: flex-start;
        gap: 8px;
        padding-bottom: 10px;
        border-bottom: 1px solid #e0e0e0;
      }
      .group-item > div:first-child {
        flex: 1 1 220px;
        min-width: 0;
      }
      .actions {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        flex: 0 0 auto;
        gap: 4px;
      }
      .actions mwc-button {
        margin-left: 0;
        --mdc-typography-button-font-size: 0.72rem;
        --mdc-button-height: 28px;
      }
        .group-name {
            font-weight: bold;
        }
        .client-institution {
            font-size: 0.9em;
            color: #555;
        }
         .group-description, .client-address {
             font-size: 0.85em;
             color: #6b6b6b;
        }
    `;
  }

  firstUpdated(props) {
    
    if (this.group) {
        this._loadGroupTeams(this.group);
    } 
  }
  
  render() {
    const topGroups = this.teams
      .filter(group => group.type === 'top')
      .sort((a, b) => a.name.localeCompare(b.name));

    const teamGroups = this.teams
      .filter(group => group.type === 'team')
      .sort((a, b) => a.name.localeCompare(b.name));
      
    return html`
      ${hasRole('superadmin', this.user_profile) || this.mode == 'pm' ? html`<mwc-button raised label=${t('teamsAdmin.add')} @click=${this._openAddDialog}></mwc-button>`: html``}

      <mwc-list>
          ${this.teams.sort((a, b) => a.name.localeCompare(b.name)).map(top => html`
          <mwc-list-item style="display: block; width: auto; height: auto;">
            <div class="group-item">
              <div style="min-width: 0;">
                <div class="group-name"><strong>${top.name}</strong></div>
                <div style="font-size: 0.7em; color: #6b6b6b;">${top.uuid}</div>
                <div class="group-description">${top.description || t('groups.noDescription')}</div>
              </div>
              <div class="actions">
                ${ this.mode == 'client' || this.mode == 'pm' ? html`
                  <mwc-button dense outlined label="${this._selectedUsersGroup && this._selectedUsersGroup.uuid == top.uuid ? t('teamsAdmin.closeUsers') : t('groups.users')}" @click=${() => this._toggleUserManagement(top)}></mwc-button>
                  <mwc-button dense outlined label=${t('common.edit')} @click=${() => this._openEditDialog(top)}></mwc-button>
                  ${hasRole('superadmin', this.user_profile) ? html`<mwc-button dense outlined label=${t('common.delete')} @click=${() => this._deleteGroup(top)}></mwc-button>`: html``}
                `: html``}
                </div>
            </div>
          </mwc-list-item>

          ${this._selectedUsersGroup && this._selectedUsersGroup.uuid == top.uuid ? html`
              ${hasRole('superadmin', this.user_profile) || this.mode == 'pm' ? html`<mwc-button outlined label="${this._editTeamMembers ? t('teamsAdmin.stopEditingMembers') : t('teamsAdmin.editMembers')}" @click=${() => { this._editTeamMembers = !this._editTeamMembers;} }></mwc-button>`: html``}
              ${this._editTeamMembers ? html`
                <div style="margin-left: 20px;">
                    <h4>${t('teamsAdmin.manageUsersFor', { name: this._selectedUsersGroup.name })}</h4>
                    <h5>${t('teamsAdmin.members')}</h5>
                    ${this._removableTeamUsers().length > 0 ? html`
                        <mwc-list>
                            ${this._removableTeamUsers().map(user => html`
                                <mwc-list-item hasMeta>
                                    <span>${user.displayName} (${user.email})</span>
                                    <mwc-button slot="meta" label=${t('common.remove')} @click=${() => this._removeUserFromTeam(user, this._selectedUsersGroup)}></mwc-button>
                                </mwc-list-item>`)}
                        </mwc-list>
                    ` : html`<p>${t('teamsAdmin.noUsers')}</p>`}

                    <h5>${t('teamsAdmin.addUsersFrom', { name: this.group.name })}</h5>
                    ${this._isLoading ? html`<p>${t('teamsAdmin.loadingUsers')}</p>` : html`
                        <mwc-list>
                            ${this._parentGroupUsers
                                .filter(parentUser => !this._teamUsers.some(teamUser => teamUser.email === parentUser.email))
                                .map(user => html`
                                    <mwc-list-item hasMeta>
                                        <span>${user.displayName} (${user.email})</span>
                                        <mwc-button slot="meta" label=${t('common.add')} @click=${() => this._addUserToTeam(user, this._selectedUsersGroup)}></mwc-button>
                                    </mwc-list-item>
                                `)}
                        </mwc-list>
                        ${this._parentGroupUsers.filter(parentUser => !this._teamUsers.some(teamUser => teamUser.email === parentUser.email)).length === 0 ? html`
                          <p>${t('teamsAdmin.allAdded')}</p>
                        ` : ''}
                    `}
                </div> 

              `: html`
                  <div style="margin-left: 20px;">
                  <h3>${t('teamsAdmin.usersOf', { name: this._selectedUsersGroup.name })}</h3>
                  <intrivity-admin-users .mode=${this.mode} .client=${this.client} .group=${this._selectedUsersGroup} .user_profile=${this.user_profile}></intrivity-admin-users>
              </div>
              `}
          `: html``}       

        `)}    
        
        

      </mwc-list>

      <mwc-dialog heading="${this._editingTeam ? t('teamsAdmin.edit') : t('teamsAdmin.add')}" ?open=${this._isDialogOpen} @closed=${this._closeDialog}>
        <mwc-textfield required style="width: 100%;" label=${t('common.name')} name="name" .value=${this._teamFormData.name} @input=${this._handleFormInput}></mwc-textfield>
        <mwc-textarea style="width: 100%;" label=${t('common.description')} name="description" .value=${this._teamFormData.description} @input=${this._handleFormInput}></mwc-textarea>
        <div style="width: 100%; display: grid; grid-template-columns: 1fr 1fr;">
            <mwc-select style="width: 100%;" data-name="status" name="status" label=${t('groups.reportStatus')}
                .value=${this._teamFormData.status} @selected=${this._handleFormInput} @closed=${e => e.stopPropagation()}>
                    <mwc-list-item value="none" ?selected=${this._teamFormData.status === 'none'}>${t('groups.statusNone')}</mwc-list-item>
                    <mwc-list-item value="process" ?selected=${this._teamFormData.status === 'process'}>${t('groups.statusProcess')}</mwc-list-item>
                    <mwc-list-item value="processed" ?selected=${this._teamFormData.status === 'processed'}>${t('groups.statusReady')}</mwc-list-item>
            </mwc-select>            
        </div>
        <mwc-select style="width: 100%;" data-name="disable_report_privacy" name="disable_report_privacy" label=${t('groups.disablePrivacyLong')}
            .value=${this._teamFormData.disable_report_privacy} @selected=${this._handleFormInput} @closed=${e => e.stopPropagation()}>
            ${[1, 0].map( s => html`<mwc-list-item value=${s} ?selected=${this._teamFormData.disable_report_privacy == s}>${s==0 ? t('common.false') : t('common.true') }</mwc-list-item>`)}
        </mwc-select>
        <mwc-select style="width: 100%;" data-name="ai_enabled" name="ai_enabled" label=${t('field.enableAi')}
            .value=${this._teamFormData.ai_enabled} @selected=${this._handleFormInput} @closed=${e => e.stopPropagation()}>
            ${[1, 0].map( s => html`<mwc-list-item value=${s} ?selected=${this._teamFormData.ai_enabled == s}>${s==0 ? t('common.false') : t('common.true') }</mwc-list-item>`)}
        </mwc-select>
        ${hasRole('superadmin', this.user_profile) ? html`
            <mwc-textfield style="width: 100%" type="datetime-local" label=${t('groups.reportDueLong')} name="due" .value=${this._teamFormData.due || ''} @input=${this._handleFormInput}></mwc-textfield>
        `: html``}
        <mwc-textfield required style="width: 100%;" disabled label=${t('groups.clientId')} name="clientid" .value=${this._teamFormData.clientid} @input=${this._handleFormInput}></mwc-textfield>
        <mwc-textfield required style="width: 100%;" disabled label=${t('teamsAdmin.parentId')} name="parent" .value=${this._teamFormData.parent} @input=${this._handleFormInput}></mwc-textfield>
        ${this._editingTeam && this._editingTeam.uuid ? html`
                <mwc-textfield style="width: 100%;"
                    label=${t('groups.groupId')}
                    name="uuid"
                    .value=${this._editingTeam.uuid}
                    readonly
                    disabled>
                </mwc-textfield>
             ` : 
             html`
                <mwc-textfield style="width: 100%;"
                    label=${t('groups.groupId')}
                    name="uuid"
                    .value=${this._teamFormData.uuid || ''}
                    @input=${this._handleFormInput}
                    ?disabled=${this._isLoading}
                    required >
                </mwc-textfield>
             `              
        }        
        <mwc-button slot="primaryAction" label=${t('common.save')} @click=${this._saveGroup}></mwc-button>
        <mwc-button slot="secondaryAction" dialogAction="cancel" label=${t('common.cancel')}></mwc-button>
      </mwc-dialog>

      <mwc-snackbar id="feedback-snackbar" labelText=${this._feedbackMessage}></mwc-snackbar>
    `;
  }
}

customElements.define('intrivity-admin-teams', IntrivityAdminTeams);
